import { apiClient } from "./apiClient";
import { endpoints } from "./endpoints";
import type { ApiRecord } from "@/types/api";

/**
 * Backend v2.1 (Sprint 4.2) Portfolio Read Model. `getCurrentPortfolio`/
 * `getSummary`/`getHoldings`/`getPositions` are genuine, zero-fabricated-
 * input reads of whatever `PortfolioPipeline` has already persisted for
 * a `portfolio_id` - no caller-supplied fill, no recomputation. This
 * replaces the Sprint 3.3-era `portfolioService.getSnapshot` contract,
 * which called what was then a write-behind-a-GET endpoint (moved to
 * `POST /portfolio/snapshot` in Sprint 4.2, wrapped below as
 * `generateSnapshot` for completeness - not invoked by any page).
 */
export const portfolioService = {
  async getCurrentPortfolio(portfolioId: string): Promise<ApiRecord> {
    const response = await apiClient.get<ApiRecord>(endpoints.portfolio.root, {
      params: { portfolio_id: portfolioId },
    });
    return response.data;
  },
  async getSummary(portfolioId: string): Promise<ApiRecord> {
    const response = await apiClient.get<ApiRecord>(endpoints.portfolio.summary, {
      params: { portfolio_id: portfolioId },
    });
    return response.data;
  },
  async getHoldings(portfolioId: string): Promise<ApiRecord[]> {
    const response = await apiClient.get<ApiRecord[]>(endpoints.portfolio.holdings, {
      params: { portfolio_id: portfolioId },
    });
    return response.data;
  },
  async getPositions(portfolioId: string): Promise<ApiRecord[]> {
    const response = await apiClient.get<ApiRecord[]>(endpoints.portfolio.positions, {
      params: { portfolio_id: portfolioId },
    });
    return response.data;
  },
  async generateSnapshot(params: PortfolioSnapshotGenerationParams): Promise<ApiRecord> {
    const response = await apiClient.post<ApiRecord>(endpoints.portfolio.snapshot, undefined, {
      params: {
        portfolio_id: params.portfolioId,
        starting_cash: params.startingCash,
        order_id: params.orderId,
        execution_id: params.executionId,
        broker_order_id: params.brokerOrderId,
        instrument_token: params.instrumentToken,
        side: params.side,
        filled_quantity: params.filledQuantity,
        fill_price: params.fillPrice,
        commission: params.commission,
      },
    });
    return response.data;
  },
};

export interface PortfolioSnapshotGenerationParams {
  portfolioId: string;
  startingCash?: number;
  orderId: string;
  executionId: string;
  brokerOrderId: string;
  instrumentToken: string;
  side: "BUY" | "SELL";
  filledQuantity: number;
  fillPrice: number;
  commission?: number;
}
